import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const SkillsContainer = styled.section`
    padding: 8rem 2rem;
    background: rgba(255, 255, 255, 0.02);
    max-width: 1200px;
    margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
    font-size: clamp(2rem, 4vw, 2.5rem);
    font-weight: 700;
    text-align: center;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
    text-align: center;
    color: #94a3b8;
    margin-bottom: 4rem;
    font-size: 1.1rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
`;

const SkillsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
`;

const SkillCategory = styled(motion.div)`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        transform: translateY(-4px);
        border-color: rgba(99, 102, 241, 0.3);
    }
`;

const CategoryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const CategoryIcon = styled.div`
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
`;

const CategoryTitle = styled.h3`
    font-size: 1.3rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
`;

const SkillsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 1.5rem 0;
`;

const SkillButton = styled.button`
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 0.5rem;
    padding: 0.5rem 1rem 0.5rem 1.5rem;
    position: relative;
    font-size: 0.95rem;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
    width: 100%;
    border-radius: 8px;
    font-family: inherit;
    
    &:before {
        content: '→';
        position: absolute;
        left: 0;
        color: #6366f1;
        font-weight: bold;
        transition: all 0.3s ease;
    }
    
    &:hover {
        background: rgba(99, 102, 241, 0.1);
        color: #6366f1;
        transform: translateX(8px);
        
        &:before {
            transform: translateX(4px);
            color: #8b5cf6;
        }
    }
`;

const CategoryDescription = styled.p`
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.5;
    font-style: italic;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 1rem;
    margin: 0;
`;

// Fun Modal Component
const ModalOverlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 2rem;
`;

const ModalContent = styled(motion.div)`
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
    border: 2px solid #6366f1;
    border-radius: 20px;
    padding: 3rem;
    max-width: 600px;
    width: 100%;
    text-align: center;
    box-shadow: 0 20px 60px rgba(99, 102, 241, 0.3);
    max-height: 80vh;
    overflow-y: auto;
`;

const ModalTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const ModalText = styled.p`
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
`;

const ModalEmoji = styled.div`
    font-size: 4rem;
    margin-bottom: 1rem;
    animation: bounce 2s infinite;
    
    @keyframes bounce {
        0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
        40% { transform: translateY(-20px); }
        60% { transform: translateY(-10px); }
    }
`;

const CloseButton = styled.button`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 1rem;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 40px rgba(99, 102, 241, 0.3);
    }
`;

const FunFact = styled.div`
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 12px;
    padding: 1rem;
    margin: 1rem 0;
    color: #6366f1;
    font-style: italic;
`;

const Skills = () => {
    const [selectedSkill, setSelectedSkill] = useState(null);

    const skillsData = [
        {
            icon: "🤖",
            title: "AI & Machine Learning",
            skills: [
                { name: "Large Language Models (GPT, BERT, T5)", key: "llm" },
                { name: "Deep Learning (TensorFlow, PyTorch)", key: "deeplearning" },
                { name: "Computer Vision (OpenCV, YOLO)", key: "cv" },
                { name: "Natural Language Processing (SpaCy, NLTK)", key: "nlp" },
                { name: "Machine Learning (Scikit-learn, XGBoost)", key: "ml" },
                { name: "Model Training & Deployment (MLflow, Docker)", key: "mlops" }
            ],
            description: "Expertise in developing and deploying production-ready AI/ML models with focus on LLMs and computer vision applications."
        },
        {
            icon: "💻",
            title: "Full-Stack Development",
            skills: [
                { name: "Frontend: React.js, Next.js, HTML5, CSS3", key: "frontend" },
                { name: "Backend: Node.js, Python (Flask, FastAPI)", key: "backend" },
                { name: "Databases: PostgreSQL, MongoDB, Redis", key: "databases" },
                { name: "APIs: RESTful, GraphQL, WebSocket", key: "apis" },
                { name: "Cloud: AWS, Azure, Google Cloud", key: "cloud" },
                { name: "DevOps: Docker, Kubernetes, CI/CD", key: "devops" }
            ],
            description: "Comprehensive full-stack development experience with modern technologies and cloud deployment."
        },
        {
            icon: "📊",
            title: "Data Science & Analytics",
            skills: [
                { name: "Data Analysis: Pandas, NumPy, Matplotlib", key: "dataanalysis" },
                { name: "Statistical Modeling: R, SciPy, Statsmodels", key: "stats" },
                { name: "Big Data: Apache Spark, Hadoop", key: "bigdata" },
                { name: "Business Intelligence: Tableau, Power BI", key: "bi" },
                { name: "Data Engineering: Apache Airflow, ETL", key: "dataeng" },
                { name: "Predictive Analytics: Time Series, Forecasting", key: "forecasting" }
            ],
            description: "Advanced data science capabilities including statistical analysis, visualization, and predictive modeling."
        },
        {
            icon: "🔬",
            title: "Research & Innovation",
            skills: [
                { name: "Neural Network Simulation (Brian2)", key: "neurosim" },
                { name: "Brain Plasticity Modeling (STDP)", key: "plasticity" },
                { name: "Research Methodology & Design", key: "research" },
                { name: "Academic Writing & Publication", key: "writing" },
                { name: "Innovation Management", key: "innovation" },
                { name: "Cross-disciplinary Collaboration", key: "collaboration" }
            ],
            description: "Research experience in neuroscience-inspired AI and cognitive modeling with practical applications."
        }
    ];

    const funContent = {
        // AI & Machine Learning
        llm: {
            emoji: "🤖",
            title: "Large Language Model Whisperer",
            content: "I speak fluent GPT, BERT, and T5! Fun fact: I once had a philosophical conversation with GPT-4 about whether AI dreams of electric sheep. Spoiler alert: It said it dreams of better training data! 🐑⚡",
            funFact: "Did you know? The largest language models have more parameters than there are stars in the Milky Way! Mind = Blown 🤯"
        },
        deeplearning: {
            emoji: "🧠",
            title: "Deep Learning Archaeologist",
            content: "I dig deep into neural networks like an archaeologist, except instead of ancient artifacts, I uncover hidden patterns in data! My favorite discovery? A neural network that learned to identify cats but somehow became obsessed with pizza. 🍕",
            funFact: "Plot twist: The first neural network was inspired by how neurons work in the brain, but now we're using it to teach computers to recognize memes!"
        },
        cv: {
            emoji: "👁️",
            title: "Computer Vision Wizard",
            content: "I teach computers to see better than humans! Well, almost. They still can't appreciate a beautiful sunset, but they can count every grain of sand on a beach in milliseconds. Trade-offs, am I right? 🏖️",
            funFact: "My computer vision models are so good, they once identified a hot dog as 'not a hot dog' with 99.9% confidence. Thanks, Silicon Valley! 🌭"
        },
        nlp: {
            emoji: "💬",
            title: "Text Whisperer & Word Wizard",
            content: "I speak to computers in their native language: confused mathematical vectors! I've trained models that understand sarcasm better than my relatives. Achievement unlocked! 🏆",
            funFact: "NLP models are like teenagers - they understand everything you say but sometimes respond in ways that make you question reality."
        },
        ml: {
            emoji: "🎯",
            title: "Machine Learning Fortune Teller",
            content: "I predict the future using math! Well, sort of. My models can tell you if an email is spam with 99% accuracy, but they can't predict why my code works on my machine but not in production. Some mysteries remain unsolved! 🔮",
            funFact: "Machine learning is like cooking - you throw a bunch of ingredients (data) into a pot (algorithm), add some magic (hyperparameters), and hope it tastes good!"
        },
        mlops: {
            emoji: "🚀",
            title: "MLOps Space Mission Commander",
            content: "I deploy machine learning models like NASA launches rockets - with lots of testing, monitoring, and a healthy fear of everything that could go wrong! My models are so well-deployed, they send me postcards from production. 📮",
            funFact: "MLOps is 10% machine learning and 90% 'Why did the model suddenly think all cats are dogs at 3 AM on a Tuesday?'"
        },

        // Full-Stack Development
        frontend: {
            emoji: "🎨",
            title: "Pixel Perfectionist",
            content: "I make websites so beautiful, they make other websites cry tears of envy! My CSS skills are so advanced, I once made a div perfectly centered on the first try. True story. 📱✨",
            funFact: "I've spent 3 hours debugging CSS only to realize I had a typo in 'color'. The life of a frontend dev is one of eternal suffering and occasional triumph!"
        },
        backend: {
            emoji: "⚙️",
            title: "Server Shepherd",
            content: "While frontend devs make things pretty, I make things actually work! I'm like the wizard behind the curtain, except instead of smoke and mirrors, I use APIs and databases. Much more reliable! 🔧",
            funFact: "Backend developers are like introverts at a party - we prefer to work behind the scenes but we're essential for everything to function!"
        },
        databases: {
            emoji: "🗄️",
            title: "Data Hoarder Supreme",
            content: "I organize data better than Marie Kondo organizes closets! My databases are so well-structured, they spark joy in other developers. SELECT happiness FROM life WHERE bugs = 0; 💫",
            funFact: "There are two types of people: those who normalize their databases and those who live in constant fear of data inconsistency."
        },
        apis: {
            emoji: "🌐",
            title: "API Diplomat",
            content: "I speak multiple API languages fluently - REST, GraphQL, WebSocket, and even the ancient dialect of SOAP (though I try not to mention that at parties). I'm basically a digital interpreter! 🤝",
            funFact: "APIs are like waiters at a restaurant - they take your order, disappear for a while, and hopefully come back with what you asked for (and not a 500 error)."
        },
        cloud: {
            emoji: "☁️",
            title: "Cloud Whisperer",
            content: "I live in the cloud, but not the fluffy kind! I deploy applications faster than you can say 'AWS Lambda cold start'. My servers are so reliable, even my coffee machine is jealous! ☕",
            funFact: "The cloud is just someone else's computer, but it's a really, really nice computer with amazing uptime!"
        },
        devops: {
            emoji: "🔄",
            title: "DevOps Zen Master",
            content: "I achieve perfect harmony between development and operations. My CI/CD pipelines are so smooth, they make jazz musicians jealous! I automate everything - even my morning coffee routine runs on Jenkins. ☯️",
            funFact: "DevOps is like being a digital plumber - nobody notices when everything works, but everyone panics when something breaks!"
        },

        // Data Science & Analytics
        dataanalysis: {
            emoji: "📈",
            title: "Data Detective Extraordinaire",
            content: "I solve mysteries hidden in spreadsheets! Armed with Pandas and NumPy, I can find patterns in data that would make Sherlock Holmes proud. Elementary, my dear Watson - it's just correlation, not causation! 🔍",
            funFact: "Data analysis is like being a digital archaeologist - you dig through layers of messy data to find the hidden treasures of insight!"
        },
        stats: {
            emoji: "📊",
            title: "Statistical Sorcerer",
            content: "I cast spells with p-values and confidence intervals! My statistical models are so accurate, they once predicted that I'd run out of coffee exactly when I did. That's when I knew I had achieved true statistical enlightenment! ⚡",
            funFact: "Statistics: The only field where you can be 95% confident about being uncertain!"
        },
        bigdata: {
            emoji: "🐘",
            title: "Big Data Elephant Trainer",
            content: "I wrangle massive datasets like a digital cowboy! Apache Spark and Hadoop are my trusty steeds, and I ride them across the vast plains of petabyte-sized data. Yeehaw! 🤠",
            funFact: "Big Data is like having a really messy garage - you know there's valuable stuff in there, but finding it requires the right tools and a lot of patience!"
        },
        bi: {
            emoji: "📋",
            title: "Business Intelligence Oracle",
            content: "I turn raw data into crystal-clear insights faster than you can say 'dashboard'! My Tableau visualizations are so beautiful, they belong in an art museum. The Louvre called - they want to display my bar charts! 🎨",
            funFact: "Business Intelligence: Making executives feel smart about data they don't understand since 1989!"
        },
        dataeng: {
            emoji: "🏗️",
            title: "Data Pipeline Architect",
            content: "I build data highways that would make civil engineers weep with joy! My ETL processes are so smooth, data flows through them like water through a perfectly engineered aqueduct. Apache Airflow is my blueprint! 🚰",
            funFact: "Data engineering: 20% building cool stuff, 80% explaining why the pipeline broke at 2 AM on Sunday."
        },
        forecasting: {
            emoji: "🔮",
            title: "Future-Seeing Algorithm Whisperer",
            content: "I predict tomorrow's trends using yesterday's data! My time series models are so accurate, they once predicted a stock market crash during my lunch break. I should probably start a hedge fund... 📈",
            funFact: "Forecasting is like weather prediction, but with more math and fewer complaints when you're wrong!"
        },

        // Research & Innovation
        neurosim: {
            emoji: "🧪",
            title: "Digital Brain Simulator",
            content: "I build virtual brains using Brian2 - and no, that's not my colleague's name! I simulate 1000+ neurons chatting with each other like a microscopic social network. Twitter has nothing on my neural networks! 🧠",
            funFact: "Neural simulation: Creating artificial brains that are smarter than their creators, one synapse at a time!"
        },
        plasticity: {
            emoji: "🔄",
            title: "Brain Plasticity Yoga Instructor",
            content: "I teach artificial neural networks to be flexible! STDP (Spike-Timing-Dependent Plasticity) is like yoga for neurons - the more they practice, the better they get at recognizing patterns. Namaste! 🧘",
            funFact: "Brain plasticity: Proof that even artificial brains can learn new tricks, unlike some humans I know!"
        },
        research: {
            emoji: "🔬",
            title: "Professional Question Asker",
            content: "I ask so many questions, Google Search started charging me premium rates! My research methodology is: Question everything, test everything, and document everything (especially the failures). 📚",
            funFact: "Research is like being a detective, except the crime is 'why doesn't this work?' and the evidence is mostly error logs."
        },
        writing: {
            emoji: "✍️",
            title: "Academic Paper Poet",
            content: "I turn complex technical concepts into readable prose! My papers are so well-written, they make technical documentation look like haikus. I'm basically the Shakespeare of scientific writing, but with more charts! 📝",
            funFact: "Academic writing: The art of using 1000 words to say what could be said in 10, while making it sound incredibly important!"
        },
        innovation: {
            emoji: "💡",
            title: "Idea Alchemist",
            content: "I transform crazy ideas into practical solutions! My innovation lab is like a mad scientist's workshop, but with better Wi-Fi and fewer explosions. Well, mostly fewer explosions... 🧨",
            funFact: "Innovation is 1% inspiration and 99% explaining why your brilliant idea won't work in production!"
        },
        collaboration: {
            emoji: "🤝",
            title: "Digital Team Harmony Conductor",
            content: "I orchestrate cross-disciplinary teams like a conductor leading a symphony! My collaboration skills are so good, I once got a neuroscientist and a software engineer to agree on something. That's basically a miracle! 🎼",
            funFact: "Collaboration: The beautiful chaos of getting experts from different fields to speak the same language (usually broken English with lots of diagrams)!"
        }
    };

    const openModal = (skillKey) => {
        setSelectedSkill(skillKey);
    };

    const closeModal = () => {
        setSelectedSkill(null);
    };

    return (
        <SkillsContainer>
            <SectionTitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                Skills & Expertise
            </SectionTitle>
            <SectionSubtitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
            >
                Click on any skill to discover my quirky take on technology! 🚀
            </SectionSubtitle>
            
            <SkillsGrid>
                {skillsData.map((category, index) => (
                    <SkillCategory
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <CategoryHeader>
                            <CategoryIcon>{category.icon}</CategoryIcon>
                            <CategoryTitle>{category.title}</CategoryTitle>
                        </CategoryHeader>
                        <SkillsList>
                            {category.skills.map((skill, skillIndex) => (
                                <li key={skillIndex}>
                                    <SkillButton 
                                        onClick={() => openModal(skill.key)}
                                        title="Click to see something fun!"
                                    >
                                        {skill.name}
                                    </SkillButton>
                                </li>
                            ))}
                        </SkillsList>
                        <CategoryDescription>{category.description}</CategoryDescription>
                    </SkillCategory>
                ))}
            </SkillsGrid>

            {/* Fun Modal */}
            <AnimatePresence>
                {selectedSkill && funContent[selectedSkill] && (
                    <ModalOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                    >
                        <ModalContent
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.5, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <ModalEmoji>{funContent[selectedSkill].emoji}</ModalEmoji>
                            <ModalTitle>{funContent[selectedSkill].title}</ModalTitle>
                            <ModalText>{funContent[selectedSkill].content}</ModalText>
                            <FunFact>
                                💡 {funContent[selectedSkill].funFact}
                            </FunFact>
                            <CloseButton onClick={closeModal}>
                                Back to Reality 🚪
                            </CloseButton>
                        </ModalContent>
                    </ModalOverlay>
                )}
            </AnimatePresence>
        </SkillsContainer>
    );
};

export default Skills;